import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { User, Moon, Sun, LogOut, Trash2, Watch, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function Profile() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [athlete, setAthlete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get("/athlete/me").then((r) => setAthlete(r.data)).catch(() => {});
  }, []);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete("/account");
      toast("Account deleted");
      logout();
      navigate("/auth");
    } catch {
      toast("Could not delete account");
      setDeleting(false);
    }
  };

  const handleLogout = () => { logout(); navigate("/auth"); };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground md:hidden" data-testid="profile-back">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="flex items-center gap-4">
        <div className="brand-gradient w-16 h-16 rounded-2xl flex items-center justify-center font-heading font-black text-white text-2xl">
          {(user?.name || "A").charAt(0).toUpperCase()}
        </div>
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight text-foreground" data-testid="profile-name">{user?.name}</h1>
          <p className="text-muted-foreground text-sm" data-testid="profile-email">{user?.email}</p>
        </div>
      </div>

      {athlete && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl border border-border bg-card p-5">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Your goal</p>
          <p className="text-foreground font-medium">{athlete.profile?.goal_text || athlete.profile?.goal_type || athlete.profile?.goal || "—"}</p>
          <p className="text-sm text-muted-foreground mt-1 capitalize">{athlete.profile?.primary_sport} · {athlete.profile?.experience_level}</p>
        </motion.div>
      )}

      {/* Preferences */}
      <div className="rounded-2xl border border-border bg-card divide-y divide-border">
        <div className="flex items-center justify-between p-5">
          <div className="flex items-center gap-3">
            {theme === "dark" ? <Moon size={20} className="text-muted-foreground" /> : <Sun size={20} className="text-muted-foreground" />}
            <span className="text-foreground font-medium">Appearance</span>
          </div>
          <Button variant="outline" size="sm" onClick={toggle} data-testid="profile-theme-toggle" className="rounded-lg">
            {theme === "dark" ? "Dark" : "Light"}
          </Button>
        </div>
        <button onClick={handleLogout} data-testid="profile-logout" className="w-full flex items-center gap-3 p-5 text-foreground hover:bg-muted/50 transition-colors">
          <LogOut size={20} className="text-muted-foreground" /> <span className="font-medium">Log out</span>
        </button>
      </div>

      {/* Import / devices */}
      <button onClick={() => navigate("/import")} data-testid="profile-import" className="w-full text-left rounded-2xl border border-border bg-card p-5 hover:border-primary/50 transition-colors">
        <div className="flex items-center gap-3">
          <Watch size={20} className="text-brand-orange" />
          <div>
            <p className="font-heading font-bold text-foreground">Garmin & data</p>
            <p className="text-sm text-muted-foreground">Connect live for automatic sync, or upload activities & wellness files (.fit/.tcx/.gpx, export ZIP).</p>
          </div>
        </div>
      </button>

      {/* Danger zone */}
      <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5">
        <p className="text-xs font-bold uppercase tracking-widest text-destructive mb-1">Danger zone</p>
        <p className="text-sm text-muted-foreground mb-4">Delete your account and all your coaching data. This can't be undone.</p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" data-testid="profile-delete-btn" className="rounded-xl">
              <Trash2 size={16} className="mr-1.5" /> Delete account
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">Delete your account?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes your profile, plan, workouts, check-ins and everything Caden has learned about you.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="profile-delete-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="profile-delete-confirm" onClick={handleDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                {deleting ? "Deleting…" : "Yes, delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
